import React, { useState, useEffect } from 'react';
import { Upload, X, Save, Trash2, Eye, Image as ImageIcon, ArrowLeft } from 'lucide-react';
import { uploadLogoToFirebase, deleteLogoFromFirebase, getPartnersFromFirebase, savePartnerToFirebase, deletePartnerFromFirebase } from './utils/firebase';

const AddLogo = () => {
  const [partners, setPartners] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    file: null,
    preview: null
  });

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      const partnersData = await getPartnersFromFirebase();
      setPartners(partnersData);
    } catch (error) {
      console.error('Error loading partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('File size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({
          ...prev,
          file,
          preview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file || !formData.name.trim()) {
      alert('Please provide both name and logo image');
      return;
    }

    setUploading(true);
    try {
      const logoUrl = await uploadLogoToFirebase(formData.file, formData.name);
      const partnerData = {
        name: formData.name.trim(),
        logo: logoUrl,
        createdAt: new Date().toISOString()
      };
      
      await savePartnerToFirebase(partnerData);
      await loadPartners();
      setFormData({ name: '', file: null, preview: null });
      
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      
      alert('Partner logo added successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Error uploading logo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (partner) => {
    if (window.confirm(`Are you sure you want to delete ${partner.name}?`)) {
      try {
        await deleteLogoFromFirebase(partner.logo);
        await deletePartnerFromFirebase(partner.id);
        await loadPartners();
        alert('Partner deleted successfully!');
      } catch (error) {
        console.error('Error deleting partner:', error);
        alert('Error deleting partner. Please try again.');
      }
    }
  };

  const clearPreview = () => {
    setFormData({ name: formData.name, file: null, preview: null });
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-emerald-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header with Back Button */}
        <div className="mb-8">
          <button
            onClick={() => window.history.back()}
            className="flex items-center text-emerald-600 hover:text-emerald-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center">
                  <ImageIcon className="w-8 h-8 mr-3" />
                  Partner Logo Management
                </h1>
                <p className="text-emerald-100 mt-2">Add and manage insurance partner logos</p>
              </div>
              <div className="bg-white/20 rounded-lg px-4 py-2">
                <span className="text-white font-semibold">{partners.length} Partners</span>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Upload Form */}
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 mb-8 border border-gray-200">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center">
                <Upload className="w-6 h-6 mr-3 text-emerald-600" />
                Add New Partner
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Partner Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                    placeholder="Enter partner name (e.g., HDFC ERGO)"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Logo Image * (Max 5MB)
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="cursor-pointer bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center shadow-lg hover:shadow-xl">
                      <Upload className="w-5 h-5 mr-2" />
                      Choose Logo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </label>
                    {formData.file && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
                          {formData.file.name}
                        </span>
                        <button
                          type="button"
                          onClick={clearPreview}
                          className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-all"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {formData.preview && (
                  <div className="mt-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Preview
                    </label>
                    <div className="w-40 h-24 bg-white rounded-xl border-2 border-gray-200 p-3 flex items-center justify-center shadow-sm">
                      <img
                        src={formData.preview}
                        alt="Logo Preview"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={uploading || !formData.file || !formData.name.trim()}
                  className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <Save className="w-5 h-5 mr-2" />
                  {uploading ? 'Uploading...' : 'Add Partner'}
                </button>
              </form>
            </div>

            {/* Partners Grid */}
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center justify-between">
                <span>Current Partners</span>
                <span className="text-sm bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full">
                  {partners.length} Total
                </span>
              </h2>
              
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
                </div>
              ) : partners.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {partners.map((partner) => (
                    <div key={partner.id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                      <div className="w-full h-24 bg-gray-50 rounded-xl mb-4 flex items-center justify-center p-3 border border-gray-100">
                        <img
                          src={partner.logo}
                          alt={partner.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <h3 className="font-semibold text-gray-800 text-center mb-4 text-lg">{partner.name}</h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => window.open(partner.logo, '_blank')}
                          className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>
                        <button
                          onClick={() => handleDelete(partner)}
                          className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </button>
                      </div>
                      {partner.createdAt && (
                        <div className="text-xs text-gray-500 text-center mt-2">
                          Added: {new Date(partner.createdAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-500 mb-2">No Partners Added Yet</h3>
                  <p className="text-gray-400">Add your first partner logo using the form above.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLogo;
